/**
 * Blink Form Six Connection Modal
 * TASK-210: OAuth flow for custodian bank connection
 * TASK-277: Coming Soon - awaiting Blink Form Six integration
 *
 * Stages:
 * 1. Info - Explain Blink Form Six (shows Coming Soon notice)
 * 2. Bank Login - Disabled until integration
 * 3. Syncing - Progress bars and loading states
 * 4. Success - Show imported data summary
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Shield,
  Lock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
  Clock
} from 'lucide-react';

interface BlinkConnectionModalProps {
  isOpen: boolean;
  custodian: 'UBS' | 'Credit Suisse' | 'Julius Baer' | 'Pictet' | 'Vontobel';
  portfolioId: string;
  onSuccess: (data: { positionsCount: number; totalValue: number }) => void;
  onClose: () => void;
}

type Stage = 'info' | 'login' | 'syncing' | 'success' | 'error';

const BlinkConnectionModal: React.FC<BlinkConnectionModalProps> = ({
  isOpen,
  custodian,
  portfolioId: _portfolioId,
  onSuccess: _onSuccess,
  onClose
}) => {
  const [stage, setStage] = useState<Stage>('info');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStep, setSyncStep] = useState('');
  const [username, setUsername] = useState('demo-user');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [syncResult, setSyncResult] = useState<{ positionsCount: number; totalValue: number } | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStage('info');
      setSyncProgress(0);
      setSyncStep('');
      setUsername('demo-user');
      setPassword('');
      setError('');
      setSyncResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get custodian branding
  const getCustodianBranding = () => {
    const branding: Record<string, { color: string; logo: string; bgGradient: string }> = {
      'UBS': {
        color: '#E60000',
        logo: '🏦',
        bgGradient: 'from-red-600 to-red-700'
      },
      'Credit Suisse': {
        color: '#0052CC',
        logo: '🏛️',
        bgGradient: 'from-blue-600 to-blue-700'
      },
      'Julius Baer': {
        color: '#FF6B00',
        logo: '🦁',
        bgGradient: 'from-orange-600 to-orange-700'
      },
      'Pictet': {
        color: '#006633',
        logo: '🌲',
        bgGradient: 'from-green-700 to-green-800'
      },
      'Vontobel': {
        color: '#8B4789',
        logo: '💎',
        bgGradient: 'from-purple-600 to-purple-700'
      }
    };
    return branding[custodian] || branding['UBS'];
  };

  const brand = getCustodianBranding();

  // Handle bank login simulation
  const handleBankLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }

    // Simulate login delay
    setStage('syncing');
    setSyncStep('Authenticating...');
    setSyncProgress(10);

    // Simulate sync process
    const steps = [
      { step: 'Authenticating...', progress: 10, delay: 800 },
      { step: 'Connecting to ' + custodian + '...', progress: 25, delay: 1000 },
      { step: 'Fetching account information...', progress: 40, delay: 1200 },
      { step: 'Downloading positions...', progress: 60, delay: 1500 },
      { step: 'Downloading cash positions...', progress: 75, delay: 1000 },
      { step: 'Downloading transactions...', progress: 90, delay: 1200 },
      { step: 'Finalizing sync...', progress: 100, delay: 500 }
    ];

    try {
      for (const { step, progress, delay } of steps) {
        await new Promise(resolve => setTimeout(resolve, delay));
        setSyncStep(step);
        setSyncProgress(progress);
      }

      // TASK-277: Blink Form Six integration is coming soon
      // Once integrated, this will call the real Blink API
      // For now, show error since this flow shouldn't be reachable
      setStage('error');
      setError('Blink Form Six integration is coming soon. Please use Pretim instead.');
    } catch (err) {
      setStage('error');
      setError('Connection failed. Please try again.');
    }
  };

  const handleClose = () => {
    if (stage === 'syncing') {
      // Prevent closing during sync
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">

        {/* Stage 1: Info */}
        {stage === 'info' && (
          <>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Connect to {custodian}</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-8">
              <div className="flex justify-center mb-6">
                <div className={`w-20 h-20 bg-gradient-to-br ${brand.bgGradient} rounded-full flex items-center justify-center text-4xl`}>
                  {brand.logo}
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center text-gray-900 mb-4">
                Blink Form Six Authentication
              </h3>

              <p className="text-center text-gray-600 mb-6">
                You'll be redirected to {custodian}'s secure e-banking login to authorize access to your portfolio data.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">What is Blink Form Six?</p>
                    <p className="text-blue-700">
                      Blink Form Six is a Swiss banking standard for secure API access. It uses OAuth 2.0 with bank-grade encryption to connect your accounts safely.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-green-600" />
                  </div>
                  <span>Bank-grade encryption (AES-256)</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Lock className="w-4 h-4 text-green-600" />
                  </div>
                  <span>OAuth 2.0 secure authorization</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  </div>
                  <span>Read-only access to portfolio data</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium mb-1">Coming Soon</p>
                    <p className="text-amber-700">
                      Blink Form Six integration with {custodian} is currently in development.
                      Use Pretim integration for portfolio data in the meantime.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                disabled
                className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed flex items-center gap-2"
              >
                <Clock className="w-4 h-4" />
                Coming Soon
              </button>
            </div>
          </>
        )}

        {/* Stage 2: Bank Login (Simulated) */}
        {stage === 'login' && (
          <>
            <div className={`px-6 py-4 bg-gradient-to-r ${brand.bgGradient} text-white flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className="text-3xl">{brand.logo}</div>
                <div>
                  <h2 className="text-xl font-semibold">{custodian} e-banking</h2>
                  <p className="text-sm opacity-90">Secure Login</p>
                </div>
              </div>
              <Lock className="w-6 h-6" />
            </div>

            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-yellow-900 mb-1">⚠️ PROTOTYPE MODE - Simulated Login</p>
                    <p className="text-yellow-800">
                      This is a demonstration. In production, you would be redirected to the real {custodian} e-banking portal.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleBankLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username / Contract Number
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Demo: Use any password to continue
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className={`w-full py-3 bg-gradient-to-r ${brand.bgGradient} text-white rounded-lg hover:opacity-90 transition-opacity font-medium`}
                >
                  Login to {custodian}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Shield className="w-4 h-4" />
                <span>Secured with SSL/TLS encryption</span>
              </div>
            </div>
          </>
        )}

        {/* Stage 3: Syncing */}
        {stage === 'syncing' && (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Syncing Portfolio Data...</h2>
            </div>

            <div className="p-8">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                {syncStep}
              </h3>
              <p className="text-center text-gray-600 mb-6">
                Please wait while we securely fetch your portfolio data from {custodian}
              </p>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
                    style={{ width: `${syncProgress}%` }}
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-2">{syncProgress}%</p>
              </div>

              {/* Sync Steps Checklist */}
              <div className="space-y-3">
                {[
                  { label: 'Authentication', done: syncProgress > 10 },
                  { label: 'Connecting to bank', done: syncProgress > 25 },
                  { label: 'Fetching positions', done: syncProgress > 40 },
                  { label: 'Downloading cash', done: syncProgress > 75 },
                  { label: 'Downloading transactions', done: syncProgress > 90 }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {item.done ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                    )}
                    <span className={`text-sm ${item.done ? 'text-gray-900' : 'text-gray-500'}`}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Stage 4: Success */}
        {stage === 'success' && syncResult && (
          <>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Successfully Connected!</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-8">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center text-gray-900 mb-2">
                Portfolio Synced!
              </h3>
              <p className="text-center text-gray-600 mb-6">
                Your {custodian} portfolio has been successfully connected
              </p>

              <div className="bg-gray-50 rounded-lg p-6 space-y-4 mb-6">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Bank</span>
                  <span className="font-semibold text-gray-900">{custodian}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Positions Imported</span>
                  <span className="font-semibold text-gray-900">{syncResult.positionsCount}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Total Portfolio Value</span>
                  <span className="font-semibold text-gray-900">
                    {new Intl.NumberFormat('de-CH', {
                      style: 'currency',
                      currency: 'CHF',
                      minimumFractionDigits: 0
                    }).format(syncResult.totalValue)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Next Sync</span>
                  <span className="font-semibold text-gray-900">Tomorrow 08:00</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  ✓ Your portfolio will automatically sync daily at 08:00 CET. You can also trigger a manual sync anytime from the portfolio card.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Portfolio
              </button>
            </div>
          </>
        )}

        {/* Stage 5: Error */}
        {stage === 'error' && (
          <>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Connection Failed</h2>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-8">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center text-gray-900 mb-2">
                Connection Failed
              </h3>
              <p className="text-center text-gray-600 mb-6">
                {error || 'Unable to connect to ' + custodian}
              </p>

              <div className="space-y-3 mb-6">
                <p className="text-sm text-gray-700">Possible reasons:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Incorrect username or password</li>
                  <li>• {custodian} servers temporarily unavailable</li>
                  <li>• Network connection issues</li>
                </ul>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setStage('login')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BlinkConnectionModal;
